import React, { useEffect, useState, useCallback } from "react";
import { apiCall } from "../../../../../../_services/apiCall";
import config from "../../../../../../config/config";
import { KpiIcon } from "../../KpiIcons";
import { isOk } from "./utils";
import KpiFormFields from "../../KpiFormFields";


/* ─────────────────────────────────────────────────────────────
   Helpers
───────────────────────────────────────────────────────────── */
const fmtDate = (iso) => {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleString(undefined, {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
};

const LEVEL_COLORS = [
  { bg: "#eff6ff", border: "#bfdbfe", dot: "#3b82f6", label: "#1d4ed8" },
  { bg: "#f0fdf4", border: "#bbf7d0", dot: "#16a34a", label: "#15803d" },
  { bg: "#fdf4ff", border: "#e9d5ff", dot: "#9333ea", label: "#7e22ce" },
  { bg: "#fff7ed", border: "#fed7aa", dot: "#ea580c", label: "#c2410c" },
  { bg: "#fdf2f8", border: "#fbcfe8", dot: "#db2777", label: "#be185d" },
];

const getColor = (level) => LEVEL_COLORS[(level - 1) % LEVEL_COLORS.length] || LEVEL_COLORS[0];

const StatusConfig = {
  APPROVED:         { bg: "#dcfce7", color: "#16a34a", border: "#bbf7d0", label: "Approved"  },
  REJECTED:         { bg: "#fee2e2", color: "#dc2626", border: "#fecaca", label: "Rejected"  },
  PENDING:          { bg: "#fef3c7", color: "#d97706", border: "#fde68a", label: "Pending"   },
  ANSWER_SUBMITTED: { bg: "#eff6ff", color: "#3b82f6", border: "#bfdbfe", label: "Submitted" },
  DEFAULT:          { bg: "#f3f4f6", color: "#6b7280", border: "#e5e7eb", label: "Unknown"   },
};

const getStatus = (s) => {
  const key = (s || "").toUpperCase();
  if (key === "ADMIN_APPROVED" || key === "ACCEPTED") return StatusConfig.APPROVED;
  return StatusConfig[key] || StatusConfig.DEFAULT;
};

/* Avatar with initials */
const Avatar = ({ name, size = 34, color }) => {
  const initials = (name || "?")
    .split(" ").filter(Boolean).slice(0, 2)
    .map((w) => w[0].toUpperCase()).join("");
  return (
    <div style={{
      width: size, height: size, minWidth: size, borderRadius: "50%",
      background: color.bg, border: `2px solid ${color.border}`,
      display: "flex", alignItems: "center", justifyContent: "center",
      fontSize: size * 0.32, fontWeight: 800, color: color.label,
      fontFamily: "var(--fm)", letterSpacing: "-.02em", flexShrink: 0,
    }}>
      {initials}
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────
   Single Audit Step — flat item from API
───────────────────────────────────────────────────────────── */
const AuditStep = ({ audit, isLast }) => {
  const color = getColor(audit.auditLevel);
  const status = getStatus(audit.status);

  return (
    <div style={{ display: "flex", gap: 0 }}>
      {/* Timeline spine */}
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", width: 36, flexShrink: 0 }}>
        <div style={{
          width: 12, height: 12, borderRadius: "50%", flexShrink: 0, marginTop: 12,
          background: color.dot, border: `3px solid ${color.border}`,
          boxShadow: `0 0 0 3px ${color.bg}`,
          zIndex: 1,
        }} />
        {!isLast && (
          <div style={{
            width: 2, flex: 1, minHeight: 20,
            background: `linear-gradient(180deg, ${color.dot}55 0%, #e5e7eb 100%)`,
            marginTop: 3,
          }} />
        )}
      </div>

      {/* Card */}
      <div style={{
        flex: 1,
        marginBottom: isLast ? 0 : 10,
        background: "#fff",
        border: `1px solid ${color.border}`,
        borderRadius: 10,
        padding: "11px 14px",
        boxShadow: "0 1px 3px rgba(0,0,0,.04)",
        position: "relative",
        overflow: "hidden",
      }}>
        {/* Level stripe */}
        <div style={{
          position: "absolute", left: 0, top: 0, bottom: 0, width: 3,
          background: color.dot, borderRadius: "10px 0 0 10px",
        }} />

        {/* Header */}
        <div style={{ display: "flex", alignItems: "flex-start", gap: 9 }}>
          <Avatar name={audit.auditorName} size={32} color={color} />

          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
              <span style={{ fontSize: 12.5, fontWeight: 700, color: "var(--ink)", fontFamily: "var(--fm)" }}>
                {audit.auditorName || "Unknown"}{audit.onBehalf ? " (Admin)" : ""}
              </span>
              <span style={{
                fontSize: 9, fontWeight: 700, color: color.label,
                background: color.bg, border: `1px solid ${color.border}`,
                borderRadius: 4, padding: "1px 6px", fontFamily: "var(--fm)",
                letterSpacing: ".06em", textTransform: "uppercase",
              }}>
                Level {audit.auditLevel}
              </span>
              {audit.auditorType && (() => {
                const isExt = (audit.auditorType || "").toUpperCase() === "EXTERNAL";
                return (
                  <span style={{
                    fontSize: 9, fontWeight: 700,
                    color: isExt ? "#7e22ce" : "#1d4ed8",
                    background: isExt ? "#faf5ff" : "#eff6ff",
                    border: `1px solid ${isExt ? "#e9d5ff" : "#bfdbfe"}`,
                    borderRadius: 4, padding: "1px 6px", fontFamily: "var(--fm)",
                    letterSpacing: ".06em", textTransform: "uppercase",
                  }}>
                    {audit.auditorType}
                  </span>
                );
              })()}
            </div>
            {audit.auditorEmail && (
              <div style={{ fontSize: 10.5, color: "var(--ink4)", marginTop: 1 }}>
                {audit.auditorEmail}
              </div>
            )}
            {audit.onBehalf && audit.onBehalfName && (
              <div style={{ fontSize: 10.5, color: "var(--ink4)", marginTop: 1, fontStyle: "italic" }}>
                on behalf of {audit.onBehalfName}
              </div>
            )}
          </div>

          {/* Status badge */}
          <div style={{
            display: "inline-flex", alignItems: "center", gap: 5,
            padding: "3px 9px", borderRadius: 20, flexShrink: 0,
            background: status.bg, border: `1px solid ${status.border}`,
            fontSize: 10.5, fontWeight: 700, color: status.color,
          }}>
            <span style={{ width: 5, height: 5, borderRadius: "50%", background: status.color, display: "inline-block" }} />
            {status.label}
          </div>
        </div>

        {/* Remark */}
        {audit.remark && (
          <div style={{
            background: "#f8f9fb", border: "1px solid var(--bdr2)",
            borderRadius: 6, padding: "7px 10px",
            fontSize: 11.5, color: "var(--ink2)", lineHeight: 1.5,
            fontStyle: "italic", marginTop: 8,
          }}>
            <span style={{ fontSize: 9, fontWeight: 700, color: "var(--ink4)", textTransform: "uppercase", letterSpacing: ".06em", fontStyle: "normal", display: "block", marginBottom: 2 }}>
              Remark
            </span>
            "{audit.remark}"
          </div>
        )}

        {/* Date */}
        {audit.auditedDate && (
          <div style={{ display: "flex", alignItems: "center", gap: 4, marginTop: 7 }}>
            <KpiIcon name="history" size={9} color="var(--ink4)" />
            <span style={{ fontSize: 10, color: "var(--ink4)" }}>{fmtDate(audit.auditedDate)}</span>
          </div>
        )}
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────
   Form Data Popup — shows answer.formData / answer.form_data
───────────────────────────────────────────────────────────── */
const FormDataPopup = ({ formData, unitMaster = [], onClose }) => {
  // formData is { [kpiType]: { [fieldKey]: value } }
  const entries = formData && typeof formData === "object" ? Object.entries(formData) : [];


  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{
          position: "fixed", inset: 0, zIndex: 1060,
          background: "rgba(15,23,42,.35)",
          backdropFilter: "blur(2px)",
        }}
      />
      {/* Modal */}
      <div style={{
        position: "fixed",
        top: "50%", left: "50%",
        transform: "translate(-50%, -50%)",
        width: "min(480px, 92vw)",
        maxHeight: "80vh",
        zIndex: 1070,
        background: "#fff",
        borderRadius: 14,
        boxShadow: "0 20px 60px rgba(0,0,0,.2)",
        display: "flex", flexDirection: "column",
        overflow: "hidden",
        fontFamily: "var(--ff)",
      }}>
        {/* Header */}
        <div style={{ padding: "16px 18px 12px", borderBottom: "1px solid var(--bdr)", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
            <div style={{ width: 34, height: 34, borderRadius: 9, background: "var(--blue3)", border: "1px solid var(--blue2)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <span style={{ fontSize: 16 }}>📋</span>
            </div>
            <div>
              <div style={{ fontSize: 13, fontWeight: 800, color: "var(--ink)", letterSpacing: "-.01em" }}>Form Data</div>
              <div style={{ fontSize: 11, color: "var(--ink4)", marginTop: 1 }}>Additional KPI-specific fields</div>
            </div>
          </div>
          <button
            onClick={onClose}
            style={{ background: "var(--bdr2)", border: "none", borderRadius: 7, width: 28, height: 28, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}
          >
            <KpiIcon name="x" size={12} color="var(--ink3)" />
          </button>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: "auto", padding: "16px 18px" }}>
          {entries.length === 0 ? (
            <div style={{ textAlign: "center", padding: "32px 16px", color: "var(--ink4)", fontSize: 12 }}>
              No form data recorded for this entry.
            </div>
          ) : (
            entries.map(([kpiKey, fields]) => (
              <div key={kpiKey} style={{ marginBottom: entries.length > 1 ? 16 : 0 }}>
                {entries.length > 1 && (
                  <div style={{ fontSize: 10, fontWeight: 700, color: "var(--blue)", textTransform: "uppercase", letterSpacing: ".07em", fontFamily: "var(--fm)", marginBottom: 10 }}>
                    {kpiKey}
                  </div>
                )}
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {typeof fields === "object" && fields !== null
                    ? Object.entries(fields).map(([fieldKey, value]) => {
                        // Handle { value, unitId } objects (unit-bearing fields)
                        const display =
                          value && typeof value === "object" && "value" in value
                            ? (() => {
                                const unitStr = value.unitId
                                  ? (unitMaster.find((u) => String(u.id) === String(value.unitId))?.unit ?? "")
                                  : "";
                                return `${value.value ?? ""}${unitStr ? ` ${unitStr}` : ""}`.trim() || "—";
                              })()
                            : String(value ?? "—");
                        return (
                          <div key={fieldKey} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 12px", background: "#f8fafc", borderRadius: 8, border: "1px solid var(--bdr2)" }}>
                            <span style={{ fontSize: 11.5, fontWeight: 600, color: "var(--ink3)", textTransform: "capitalize" }}>
                              {fieldKey.replace(/_/g, " ")}
                            </span>
                            <span style={{ fontSize: 13, fontWeight: 700, color: "var(--ink)", fontFamily: "var(--fm)" }}>
                              {display}
                            </span>
                          </div>
                        );
                      })
                    : (
                      <div style={{ fontSize: 12, color: "var(--ink4)" }}>{String(fields ?? "—")}</div>
                    )}
                </div>
              </div>
            ))
          )}
        </div>

        {/* Footer */}
        <div style={{ borderTop: "1px solid var(--bdr)", background: "#fafbfc", padding: "10px 18px", display: "flex", justifyContent: "flex-end" }}>
          <button className="ahp-btn ahp-btn-g" onClick={onClose} style={{ fontSize: 11.5 }}>Close</button>
        </div>
      </div>
    </>
  );
};


/* ─────────────────────────────────────────────────────────────
   Main Modal — centered on screen
───────────────────────────────────────────────────────────── */
const AuditTrailModal = ({ show, onHide, answer, questionId, financialYearId, locs }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState([]); // flat array from API
  const [showFormData, setShowFormData] = useState(false);
  const [unitMaster, setUnitMaster] = useState([]); // [{ id, unit, unit_name }]

  // Fetch unit master once on mount
  useEffect(() => {

    apiCall(`${config.POSTLOGIN_API_URL_COMPANY}getUnitMaster`, {}, {}, "GET", false, false)
      .then(({ isSuccess, data: d }) => {
        if (isSuccess) {
          const list = Array.isArray(d) ? d : Array.isArray(d?.data) ? d.data : [];
          setUnitMaster(list);
        }
      })
      .catch(() => {});
  }, []);

  const load = useCallback(async () => {
    if (!answer || !questionId || !financialYearId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await apiCall(
        `${config.POSTLOGIN_API_URL_COMPANY}getKpiAuditTrail`,
        {},
        { questionId, financialYearId, answerId: answer?.id },
        "GET"
      );
      if (isOk(res)) {
        const raw = res?.data?.data ?? res?.data ?? [];
        setData(Array.isArray(raw) ? raw : []);
      } else {
        setError(res?.data?.message || "Failed to load audit trail.");
      }
    } catch (err) {
      setError(`Network error: ${err?.message || String(err)}`);
    } finally {
      setLoading(false);
    }
  }, [answer, questionId, financialYearId]);

  useEffect(() => {
    if (show) load();
    else { setData([]); setError(null); setShowFormData(false); }
  }, [show, load]);

  // Close on Escape
  useEffect(() => {
    if (!show) return;
    const handler = (e) => { if (e.key === "Escape") onHide(); };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [show, onHide]);

  if (!show) return null;

  const locName = locs?.find(
    (l) => String(l.id) === String(answer?.location_id ?? answer?.locationId)
  )?.name;
  const fromDate = (answer?.from_date || answer?.fromDate || "")?.slice(0, 10);
  const toDate   = (answer?.to_date   || answer?.toDate   || "")?.slice(0, 10);

  // Derive overall status from flat list
  const overallStatus = (() => {
    if (!data.length) return "PENDING";
    const statuses = data.map((a) => {
      const s = (a.status || "").toUpperCase();
      return (s === "ADMIN_APPROVED" || s === "ACCEPTED") ? "APPROVED" : s;
    });
    if (statuses.includes("REJECTED")) return "REJECTED";
    const nonSubmitted = statuses.filter((s) => s !== "ANSWER_SUBMITTED");
    if (nonSubmitted.length && nonSubmitted.every((s) => s === "APPROVED")) return "APPROVED";
    return "PENDING";
  })();

  const overallCfg = getStatus(overallStatus);
  const uniqueLevels = [...new Set(data.map((d) => d.auditLevel))].sort();

  return (
    <>
      {/* ── Backdrop ── */}
      <div
        onClick={onHide}
        style={{
          position: "fixed", inset: 0, zIndex: 1040,
          background: "rgba(15,23,42,.5)",
          backdropFilter: "blur(3px)",
          animation: "atFadeIn .15s ease",
        }}
      />

      {/* ── Centered Modal ── */}
      <div style={{
        position: "fixed",
        top: "50%", left: "50%",
        transform: "translate(-50%, -50%)",
        width: "min(560px, 94vw)",
        maxHeight: "85vh",
        zIndex: 1050,
        background: "var(--bg, #f7f8fc)",
        display: "flex", flexDirection: "column",
        borderRadius: 16,
        boxShadow: "0 24px 64px rgba(0,0,0,.18), 0 4px 16px rgba(0,0,0,.10)",
        animation: "atPopIn .2s cubic-bezier(.34,1.56,.64,1)",
        fontFamily: "var(--ff)",
        overflow: "hidden",
      }}>

        {/* ── Header ── */}
        <div style={{
          background: "#fff",
          borderBottom: "1px solid var(--bdr)",
          padding: "16px 20px",
          flexShrink: 0,
        }}>
          <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
            {/* Icon */}
            <div style={{
              width: 40, height: 40, borderRadius: 11, flexShrink: 0,
              background: "var(--blue3)", border: "1px solid var(--blue2)",
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              <KpiIcon name="audit" size={18} color="var(--blue)" />
            </div>

            {/* Title */}
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: "var(--blue)", textTransform: "uppercase", letterSpacing: ".09em", fontFamily: "var(--fm)", marginBottom: 3 }}>
                Audit Trail
              </div>
              <div style={{ fontSize: 15, fontWeight: 800, color: "var(--ink)", letterSpacing: "-.02em", lineHeight: 1.25 }}>
                {locName || `Answer #${answer?.id}`}
              </div>
              {(fromDate || toDate) && (
                <div style={{ fontSize: 11, color: "var(--ink4)", marginTop: 2 }}>
                  {fromDate}{toDate ? ` → ${toDate}` : ""}
                </div>
              )}
            </div>

            {/* Overall status + close btn */}
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
              {!loading && data.length > 0 && (
                <span style={{
                  display: "inline-flex", alignItems: "center", gap: 5,
                  padding: "4px 10px", borderRadius: 20,
                  background: overallCfg.bg, border: `1px solid ${overallCfg.border}`,
                  fontSize: 11, fontWeight: 700, color: overallCfg.color,
                }}>
                  <span style={{ width: 6, height: 6, borderRadius: "50%", background: overallCfg.color, display: "inline-block" }} />
                  {overallCfg.label}
                </span>
              )}
              <button
                onClick={onHide}
                style={{
                  background: "var(--bdr2)", border: "none", borderRadius: 7,
                  width: 28, height: 28, cursor: "pointer",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  flexShrink: 0, transition: "background .15s",
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = "var(--bdr)"}
                onMouseLeave={(e) => e.currentTarget.style.background = "var(--bdr2)"}
                title="Close (Esc)"
              >
                <KpiIcon name="x" size={12} color="var(--ink3)" />
              </button>
            </div>
          </div>

          {/* Meta strip — shown only when data is loaded */}
          {!loading && data.length > 0 && (
            <div style={{
              display: "flex", gap: 0, marginTop: 12,
              borderRadius: 8, overflow: "hidden",
              border: "1px solid var(--bdr2)",
            }}>
              {[
                { label: "Total Steps", value: data.length },
                { label: "Audit Levels", value: uniqueLevels.join(", ") },
                { label: "Question ID", value: `#${questionId}` },
              ].map(({ label, value }, i, arr) => (
                <div key={label} style={{
                  flex: 1, padding: "8px 12px",
                  background: i % 2 === 0 ? "#f4f6fa" : "#f8f9fb",
                  borderRight: i < arr.length - 1 ? "1px solid var(--bdr2)" : "none",
                  textAlign: "center",
                }}>
                  <div style={{ fontSize: 9, fontWeight: 700, color: "var(--ink4)", textTransform: "uppercase", letterSpacing: ".07em", fontFamily: "var(--fm)" }}>{label}</div>
                  <div style={{ fontSize: 13, fontWeight: 800, color: "var(--ink)", fontFamily: "var(--fm)", marginTop: 2 }}>{value}</div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* ── Body ── */}
        <div style={{ flex: 1, overflow: "auto", padding: "16px 20px 20px" }}>

          {/* Loading */}
          {loading && (
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 12, padding: "48px 0" }}>
              <div style={{
                width: 28, height: 28, border: "3px solid var(--bdr)",
                borderTopColor: "var(--blue)", borderRadius: "50%",
                animation: "atSpin 0.7s linear infinite",
              }} />
              <span style={{ fontSize: 12, color: "var(--ink4)" }}>Loading audit trail…</span>
            </div>
          )}

          {/* Error */}
          {!loading && error && (
            <div style={{ padding: "32px 0", textAlign: "center" }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: "var(--red)", marginBottom: 8 }}>Failed to load audit trail</div>
              <div style={{ fontSize: 12, color: "var(--ink4)", marginBottom: 16 }}>{error}</div>
              <button className="ahp-btn ahp-btn-g" onClick={load}>
                <KpiIcon name="history" size={12} color="var(--ink3)" /> Retry
              </button>
            </div>
          )}

          {/* Empty */}
          {!loading && !error && data.length === 0 && (
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", padding: "48px 16px", gap: 10, textAlign: "center" }}>
              <div style={{ width: 48, height: 48, borderRadius: 14, background: "var(--blue3)", border: "1px solid var(--blue2)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <KpiIcon name="audit" size={22} color="var(--blue)" />
              </div>
              <div style={{ fontSize: 13, fontWeight: 700, color: "var(--ink2)" }}>No audit trail found</div>
              <div style={{ fontSize: 12, color: "var(--ink4)", maxWidth: 260 }}>
                No audit steps have been recorded for this answer yet.
              </div>
            </div>
          )}

          {/* ── Timeline — flat array rendered directly ── */}
          {!loading && !error && data.length > 0 && (
            <div style={{ paddingLeft: 2 }}>
              {data.map((audit, i) => (
                <AuditStep
                  key={i}
                  audit={audit}
                  isLast={i === data.length - 1}
                />
              ))}
            </div>
          )}
        </div>

        {/* ── Footer ── */}
        <div style={{
          borderTop: "1px solid var(--bdr)", background: "#fff",
          padding: "11px 20px", flexShrink: 0,
          display: "flex", alignItems: "center", justifyContent: "space-between",
        }}>
          {/* Form Data button — only shown when answer has form_data */}
          {(answer?.formData || answer?.form_data) ? (
            <button

              className="ahp-btn ahp-btn-g"
              onClick={() => setShowFormData(true)}
              style={{ fontSize: 11.5, display: "inline-flex", alignItems: "center", gap: 5 }}
            >
              <span>📋</span> View Form Data
            </button>
          ) : <div />}

          <button className="ahp-btn ahp-btn-g" onClick={onHide} style={{ fontSize: 11.5 }}>
            Close
          </button>
        </div>
      </div>

      {/* Form Data Popup */}
      {showFormData && (
        <FormDataPopup
          formData={answer?.formData ?? answer?.form_data ?? {}}
          unitMaster={unitMaster}
          onClose={() => setShowFormData(false)}
        />
      )}

      <style>{`
        @keyframes atPopIn {
          from { transform: translate(-50%, -48%) scale(.95); opacity: 0; }
          to   { transform: translate(-50%, -50%) scale(1);   opacity: 1; }
        }
        @keyframes atFadeIn {
          from { opacity: 0; }
          to   { opacity: 1; }
        }
        @keyframes atSpin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </>
  );
};

export default AuditTrailModal;